/**
 * @fileoverview Profile Avatar utility for rendering user profile pictures.
 * Handles displaying either a user's profile picture (from SSO) or a fallback
 * initial letter avatar.
 *
 * @module utils/profileAvatar
 */

import { isGuestName } from '../core/utils.js';

/**
 * Default sizes for profile avatars
 * @type {Object.<string, number>}
 */
export const AVATAR_SIZES = {
  small: 24,   // Sidebar, compact lists
  medium: 32,  // Chat messages, cards
  large: 48,   // Profile headers
  xlarge: 64   // Profile pages
};

/**
 * Creates a profile avatar element (either an image or SVG initial).
 *
 * @param {Object} user - User object containing profile data
 * @param {string} [user.profilePictureUrl] - URL to user's profile picture
 * @param {string} [user.firstName] - User's first name
 * @param {string} [user.name] - User's full name
 * @param {string} [user.username] - User's username
 * @param {Object} [options={}] - Configuration options
 * @param {number|string} [options.size=24] - Size in pixels or key from AVATAR_SIZES
 * @param {string} [options.className=''] - Additional CSS classes to add
 * @param {number} [options.fontSize] - Optional initial-letter font size override
 * @returns {HTMLElement} Container element with the avatar
 *
 * @example
 * // Create a small avatar for sidebar
 * const avatar = createProfileAvatar(user, { size: 'small' });
 * sidebarButton.appendChild(avatar);
 *
 * @example
 * // Create a larger avatar for profile page
 * const avatar = createProfileAvatar(user, { size: 64, className: 'profile-page__avatar' });
 */
export function createProfileAvatar(user, options = {}) {
  const size = typeof options.size === 'string'
    ? AVATAR_SIZES[options.size] || AVATAR_SIZES.small
    : options.size || AVATAR_SIZES.small;
  const className = options.className || '';
  const { fontSize } = options;

  const container = document.createElement('span');
  container.className = `profile-avatar profile-avatar--${size}px ${className}`.trim();
  container.setAttribute('data-size', size);

  replaceAvatarContent(container, user, size, fontSize);

  return container;
}

/**
 * Updates an existing profile avatar container with new user data.
 *
 * @param {HTMLElement} container - The avatar container element to update
 * @param {Object} user - User object containing profile data
 * @param {string} [user.profilePictureUrl] - URL to user's profile picture
 * @param {string} [user.firstName] - User's first name
 * @param {string} [user.name] - User's full name
 * @param {string} [user.username] - User's username
 * @returns {void}
 *
 * @example
 * // Update sidebar avatar when user logs in
 * const container = document.querySelector('.profile-avatar');
 * updateProfileAvatar(container, currentUser);
 */
export function updateProfileAvatar(container, user) {
  if (!container) return;

  const size = parseInt(container.getAttribute('data-size'), 10) || AVATAR_SIZES.small;
  replaceAvatarContent(container, user, size);
}

/**
 * Gets the display initial for a user.
 *
 * @param {Object} user - User object
 * @param {string} [user.firstName] - User's first name
 * @param {string} [user.name] - User's full name
 * @param {string} [user.username] - User's username
 * @returns {string} Single uppercase letter or empty string
 */
export function getUserInitial(user) {
  if (!user) return '';

  let displayName = user.firstName || user.name || user.username;

  // Don't use guest-style names for the initial
  if (isGuestName(displayName)) {
    displayName = '';
  }

  return displayName ? displayName.charAt(0).toUpperCase() : '';
}

/**
 * Replace a profile-avatar container's child with the canonical image or
 * fallback SVG renderer.
 *
 * @private
 * @param {HTMLElement} container - Avatar container to populate
 * @param {Object} user - User object containing profile data
 * @param {number} size - Avatar size in pixels
 * @param {number} [fontSize] - Optional initial-letter font size override
 */
function replaceAvatarContent(container, user, size, fontSize) {
  const fallback = () => {
    container.replaceChildren(createInitialSvg(user, size, fontSize));
  };

  if (user?.profilePictureUrl) {
    container.replaceChildren(createImageElement(user.profilePictureUrl, size, fallback));
    return;
  }

  fallback();
}

/**
 * Creates an image element for profile picture.
 *
 * @private
 * @param {string} src - Image source URL
 * @param {number} size - Size in pixels
 * @param {Function} onError - Callback when image fails to load
 * @returns {HTMLImageElement}
 */
function createImageElement(src, size, onError) {
  const img = document.createElement('img');
  img.alt = 'Profile';
  img.className = 'profile-avatar__image';
  img.width = size;
  img.height = size;
  // Avoid sending referrer to prevent rate limiting from external image hosts
  img.referrerPolicy = 'no-referrer';
  img.onerror = onError;
  // Also check on load that the image actually rendered (catches HTTP errors)
  img.onload = () => {
    if (img.naturalWidth === 0) {
      onError();
    }
  };
  // Set src last to ensure event handlers are attached first
  img.src = src;
  return img;
}

/**
 * Creates an SVG element displaying user's initial, or a generic user icon
 * if no initial is available (e.g., guest users without a name).
 *
 * @private
 * @param {Object} user - User object
 * @param {number} size - Size in pixels
 * @param {number} [fontSize] - Optional initial-letter font size override
 * @returns {SVGElement}
 */
function createInitialSvg(user, size, fontSize) {
  const initial = getUserInitial(user);

  // If no initial available, show generic user icon
  if (!initial) {
    return createUserIconSvg(size);
  }

  const resolvedFontSize = fontSize ?? Math.round(size * 0.42); // Scale font relative to size
  const center = size / 2;
  const radius = size * 0.42; // Circle takes up ~84% of the viewBox

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', size);
  svg.setAttribute('height', size);
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('class', 'profile-avatar__initial');

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', center);
  circle.setAttribute('cy', center);
  circle.setAttribute('r', radius);
  circle.setAttribute('fill', 'currentColor');

  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text.setAttribute('x', center);
  text.setAttribute('y', center);
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('class', 'profile-avatar__initial-text');
  text.setAttribute('font-size', resolvedFontSize);
  text.textContent = initial;

  svg.appendChild(circle);
  svg.appendChild(text);

  return svg;
}

/**
 * Creates an SVG element with a generic user icon (uses sprite).
 *
 * @private
 * @param {number} size - Size in pixels
 * @returns {SVGElement}
 */
function createUserIconSvg(size) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', size);
  svg.setAttribute('height', size);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'profile-avatar__icon');

  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#icon-user');

  svg.appendChild(use);

  return svg;
}
